import { useId, useState } from "react";
import { shortReference } from "../../../shared/references/shortReferences";
import { useTranslation } from "react-i18next";
import type { ApiSchema } from "../../../api/apiSchema";
import { decodeSelector, type ItemSelector, type Selector } from "../../../api/content/authoring/selectorDecoders";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { readableJsonText } from "../../../shared/formatting/readableJson";
import { itemIdOf } from "../../../api/content/processDefinitions";
import { ITEM_KINDS, type ItemKind } from "../../../api/content/reviewDecoders";
import { LibraryPicker } from "../../search";
import { listedItem, type AuthoringOptions } from "../draft/authoringOptions";
import { itemSelectorOf, selectedItemId } from "./selectorChoices";
import { CONTROL_ROW_CLASS, WIDE_CONTROL_ROW_CLASS } from "../shared/authoringStyles";
import OptionLabel from "../../../ui/forms/OptionLabel";
import Field from "../../../ui/forms/Field";

const RENDERINGS: readonly ApiSchema<"SubjectRendering">[] = ["plain", "numbered_segments"];
const EXAMPLE_INCLUSIONS: readonly ApiSchema<"ExampleInclusion">[] = ["none", "matching_subject", "all"];

/** The parameters of one selector, by its kind (P-6). Kinds without a form are edited as JSON. */
export default function SelectorFields({ selector, options, onChange, label }: { selector: Selector; options: AuthoringOptions; onChange: (next: Selector) => void; label: string }) {
  const { t } = useTranslation();
  switch (selector.kind) {
    case "earlier_results":
    case "accepted_instance_data":
    case "reference_vocabularies": return null;
    case "item":
      return <ItemChoice label={t("authoring.selector.item", { name: label })} selector={selector.item} kinds={ITEM_KINDS} options={options} onChange={(item) => { onChange({ ...selector, item }); }} />;
    case "label_list":
      return (
        <div className="flex flex-wrap items-center gap-3">
          <ItemChoice label={t("authoring.selector.labelList", { name: label })} selector={selector.label_list} kinds={LABEL_LISTS} options={options} onChange={(item) => { onChange({ ...selector, label_list: item }); }} />
          <OptionLabel>
            <input type="checkbox" checked={selector.include_guidelines} onChange={(event) => { onChange({ ...selector, include_guidelines: event.target.checked }); }} />
            {t("authoring.selector.includeGuidelines")}
          </OptionLabel>
          <Field layout="inline" label={t("authoring.selector.includeExamples")} className="text-xs">
            {(control) => (
              <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={selector.include_examples} onChange={(event) => { onChange({ ...selector, include_examples: EXAMPLE_INCLUSIONS.find((value) => value === event.target.value) ?? "none" }); }}>
                {EXAMPLE_INCLUSIONS.map((value) => <option key={value} value={value}>{t(`authoring.selector.examples.${value}`)}</option>)}
              </select>
            )}
          </Field>
        </div>
      );
    case "subject_text":
      return selector.scope === "selection" ? <RawSelector selector={selector} onChange={onChange} label={label} /> : <SubjectTextFields selector={selector} onChange={onChange} label={label} />;
    case "source_version":
      return <SourceVersionFields selector={selector} onChange={onChange} />;
    case "competency_questions":
    case "ontology_terms":
    case "few_shot_examples":
    case "source_scope":
    case "derived_records":
    case "corpus_documents":
      return <RawSelector selector={selector} onChange={onChange} label={label} />;
  }
}

function SourceVersionFields({ selector, onChange }: { selector: ApiSchema<"SourceVersionSelector">; onChange: (next: Selector) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => selector.paths.join("\n"));
  const [invalid, setInvalid] = useState(false);
  return (
    <Field
      label={t("authoring.selector.sourceVersionPaths")}
      hint={t("authoring.selector.sourceVersionPathsHint")}
      errors={invalid ? <p className="app-error-text text-xs" role="alert">{t("authoring.selector.sourceVersionPathsInvalid")}</p> : undefined}
    >
        {(control) => (
          <textarea
            {...control}
            className={`${FORM_COMPACT_CONTROL_CLASS} font-mono text-xs`}
            rows={4}
            aria-invalid={invalid}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              try {
                onChange(decodeSelector({ ...selector, paths: event.target.value.split(/\r?\n/).filter((path) => path !== "") }));
                setInvalid(false);
              } catch {
                setInvalid(true);
              }
            }}
          />
        )}
    </Field>
  );
}

function SubjectTextFields({ selector, onChange, label }: { selector: ApiSchema<"SubjectTextSelector">; onChange: (next: Selector) => void; label: string }) {
  const { t } = useTranslation();
  const segmentType = typeof selector.scope === "string" ? null : selector.scope.segment_type;
  return (
    <div className={WIDE_CONTROL_ROW_CLASS}>
      <Field layout="inline" label={t("authoring.selector.scope")}>
        {(control) => (
          <select
            {...control}
            className={`${FORM_SELECT_CLASS} w-auto`}
            aria-label={t("authoring.selector.scopeOf", { name: label })}
            value={segmentType === null ? "document" : "segments"}
            onChange={(event) => {
              onChange({ ...selector, scope: event.target.value === "document" ? "document" : { segment_type: segmentType ?? "paragraph" } });
            }}
          >
            <option value="document">{t("authoring.selector.wholeDocument")}</option>
            <option value="segments">{t("authoring.selector.eachSegment")}</option>
          </select>
        )}
      </Field>
      {segmentType === null ? null : (
        <Field layout="inline" label={t("authoring.selector.segmentType")}>
          {(control) => (
            <input {...control} className={`${FORM_COMPACT_CONTROL_CLASS} w-32 font-mono`} value={segmentType} onChange={(event) => { onChange({ ...selector, scope: { segment_type: event.target.value } }); }} />
          )}
        </Field>
      )}
      <Field layout="inline" label={t("authoring.selector.render")}>
        {(control) => (
          <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={selector.render} onChange={(event) => { onChange({ ...selector, render: RENDERINGS.find((value) => value === event.target.value) ?? "plain" }); }}>
            {RENDERINGS.map((value) => <option key={value} value={value}>{t(`authoring.selector.renderings.${value}`)}</option>)}
          </select>
        )}
      </Field>
    </div>
  );
}

const LABEL_LISTS: readonly ItemKind[] = ["label_list"];

/**
 * An item to read, floating (the Thread's content version decides) or pinned to its version at
 * `main`, chosen with the library picker (F38): DMW's search syntax and a filter by kind or
 * artifact type, over the project's items at `main` as the form's list has them.
 */
export function ItemChoice({ label, selector, kinds, options, onChange }: { label: string; selector: ItemSelector; kinds: readonly ItemKind[]; options: AuthoringOptions; onChange: (next: ItemSelector) => void }) {
  const { t } = useTranslation();
  const itemId = selectedItemId(selector);
  const pinned = "version" in selector;
  const listed = listedItem(options, itemId);
  const chosenText = itemId === "" ? t("authoring.selector.chooseItem") : listed?.name ?? t("authoring.selector.notAtMain", { item: shortReference("itemId", itemId) });
  return (
    <span className={CONTROL_ROW_CLASS}>
      <LibraryPicker
        projectId={options.projectId}
        kinds={kinds}
        buttonLabel={chosenText}
        buttonAccessibleName={`${label}: ${chosenText}`}
        searchLabel={t("authoring.selector.findItem")}
        onPick={(item) => { onChange(itemSelectorOf(itemIdOf(item.reference), pinned ? item.reference : null)); }}
      />
      <OptionLabel>
        <input
          type="checkbox"
          checked={pinned}
          disabled={listed === undefined}
          onChange={(event) => {
            onChange(itemSelectorOf(itemId, event.target.checked && listed !== undefined ? listed.reference : null));
          }}
        />
        {t("authoring.selector.pinned")}
      </OptionLabel>
      <span className="app-help-text">{pinned ? t("authoring.selector.pinnedHint") : t("authoring.selector.floatingHint")}</span>
    </span>
  );
}

/** Selectors without a form of their own: their JSON, checked as a selector on every change. */
function RawSelector({ selector, onChange, label }: { selector: Selector; onChange: (next: Selector) => void; label: string }) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState(() => readableJsonText(selector));
  const [invalid, setInvalid] = useState(false);
  return (
    <div className="space-y-1">
      <textarea
        id={id}
        className={`${FORM_COMPACT_CONTROL_CLASS} font-mono text-xs`}
        rows={5}
        aria-label={t("authoring.selector.json", { name: label })}
        aria-invalid={invalid}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          try {
            onChange(decodeSelector(JSON.parse(event.target.value)));
            setInvalid(false);
          } catch {
            setInvalid(true);
          }
        }}
      />
      {invalid ? <p className="app-error-text text-xs">{t("authoring.selector.jsonInvalid")}</p> : null}
    </div>
  );
}
